import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import api from 'api/api';
import useVersionInfo from 'hooks/useVersionInfo';

import { V4_ACTIVITY_TYPE_MAP } from './constants';
import { ActivityType, SpoldDataFetchParams, SpoldDataResponse, SpoldSearchParams } from './types';

const normalizeActivityType = (type: string): ActivityType =>
  V4_ACTIVITY_TYPE_MAP[type.toLowerCase()] ?? (type as ActivityType);

const normalizeActivityTypes = (data: SpoldDataResponse): SpoldDataResponse => ({
  ...data,
  activities: data.activities?.map((a) => ({ ...a, activity_type: normalizeActivityType(a.activity_type) })),
  products: data.products?.map((p) => ({
    ...p,
    activities: p.activities.map((a) => ({ ...a, activity_type: normalizeActivityType(a.activity_type) })),
  })),
});

const fetchSpoldData = async (params: SpoldDataFetchParams) => {
  const response = await api.post<SpoldDataResponse>(`/search/${params.ver}/${params.system_model}`, params);
  return normalizeActivityTypes(response.data);
};

const useSpoldData = (
  params: SpoldSearchParams,
  options?: Omit<UseQueryOptions<SpoldDataResponse, AxiosError>, 'queryKey' | 'queryFn'>
) => {
  const { ver, system_model } = useVersionInfo();
  return useQuery<SpoldDataResponse, AxiosError>({
    queryKey: ['search', ver, system_model, params],
    queryFn: () => fetchSpoldData({ ...params, ver, system_model }),
    ...options,
  });
};

export default useSpoldData;
